import { App } from '@capacitor/app';
import { requireBiometricUnlock } from 'app/services/biometricGate';
import {
  BIOMETRIC_IDLE_TIMEOUT_MS,
  UNLOCK_RESULT,
} from 'app/services/biometricGate/biometricGate.constants';
import deviceTelemetry from 'app/services/deviceTelemetry';
import { jwtManager } from 'app/services/jwtService';
import { getKeepMeSignedIn } from 'app/services/secureTokenStore';
import { hasResumableSession } from 'app/services/signalR/signalR.utils';

import { logger } from 'utils/logger';
import { installKeyboardVisibilityClass } from 'utils/native/keyboardVisibility';
import { lockViewportZoom } from 'utils/native/lockViewportZoom';
import { applyNativePlatformClass } from 'utils/native/nativePlatformClass';
import { installNativeTelemetry } from 'utils/native/nativeTelemetry';
import { installNativePasskeyShim } from 'utils/native/passkeyShim';
import { getIsNativePlatform } from 'utils/platform';

import { hideUnlockScreen, showUnlockScreen } from './unlockScreen';

const resumeGateState = {
  backgroundedAt: null,
  isReauthInFlight: false,
};

const clearSession = reason => {
  logger.info(`[NativeBoot] ${reason} — clearing stored session`);
  jwtManager.setToken(null);
};

const applyUnlockResult = (result, context) => {
  if (result === UNLOCK_RESULT.DENIED) {
    clearSession(`${context}: unlock denied`);

    return false;
  }

  if (result === UNLOCK_RESULT.NO_SECURITY) {
    logger.warn(`[NativeBoot] ${context}: device unsecured — session kept without re-auth`);
  }

  return true;
};

const registerResumeGate = () => {
  App.addListener('appStateChange', async ({ isActive }) => {
    if (!isActive) {
      resumeGateState.backgroundedAt = Date.now();

      return;
    }

    const idleFor =
      resumeGateState.backgroundedAt === null ? 0 : Date.now() - resumeGateState.backgroundedAt;
    resumeGateState.backgroundedAt = null;

    if (idleFor < BIOMETRIC_IDLE_TIMEOUT_MS) {
      return;
    }

    if (resumeGateState.isReauthInFlight || !hasResumableSession()) {
      return;
    }

    resumeGateState.isReauthInFlight = true;
    showUnlockScreen();

    try {
      const result = await requireBiometricUnlock();
      applyUnlockResult(result, 'resume');
    } finally {
      hideUnlockScreen();
      resumeGateState.isReauthInFlight = false;
    }
  });
};

export const runNativeBoot = async () => {
  if (!getIsNativePlatform()) {
    return;
  }

  // Capture native JS errors in App Insights (the full SDK is OOM-disabled in the shell) — install
  // FIRST so failures in the rest of boot are reported too (DEV-4084).
  installNativeTelemetry();

  // Suppress the iOS focus-zoom (a focused <16px input magnifies the whole page) and reflect the
  // keyboard's visibility on <html> so the login layout can compact + top-align the card. Both are
  // native-only and must be wired before the login form renders.
  lockViewportZoom();
  installKeyboardVisibilityClass();
  applyNativePlatformClass();

  // Install the native passkey shim on every native boot — sign-in (passkey) happens before any
  // session exists, so it must be wired regardless of the resumable-session checks below (DEV-4084).
  installNativePasskeyShim();

  deviceTelemetry.pingOta();

  registerResumeGate();

  // A VoIP-push / background wake launches the app while it is NOT foreground (isActive=false). The
  // cold-boot session gating below (clear-if-not-keep-signed-in + biometric unlock) is for a USER-
  // initiated foreground launch — running it on a background push wake would clear the session
  // mid-call, logging the user out and killing telephony auth so the incoming call can't connect
  // (DEV-4332). Skip it here and let registerResumeGate re-gate when the user actually foregrounds.
  const { isActive } = await App.getState();

  if (!isActive) {
    logger.info('[NativeBoot] background launch (push wake) — deferring session gating to resume');

    return;
  }

  if (!getKeepMeSignedIn()) {
    clearSession('keepMeSignedIn not set');

    return;
  }

  if (!hasResumableSession()) {
    return;
  }

  showUnlockScreen();

  try {
    const result = await requireBiometricUnlock();
    applyUnlockResult(result, 'cold-boot');
  } finally {
    hideUnlockScreen();
  }
};
